import React, { useState } from 'react';
import { Modal, FormField, Button, AddressInput, inputCls, NumberField, ChoiceKeys, TripStrip } from '../ui';
import { useStore } from '../../store/StoreContext';
import { QUICK_DISPATCH_TYPES, QUICK_DISPATCH_DEFAULTS, TRUCK_TYPE_OPTIONS_COMPACT } from '../../lib/constants';
import { currentMoveFor, optionsForMove, todayYmd } from '../../lib/moves';
import db from '../../lib/database';

// ===== QUICK DISPATCH MODAL (2026-08-06, Jaylen) =====
// The small-job fast path: delivery / dump run / labor only / pickup jobs
// skip the estimate funnel — this one form creates a minimal already-booked
// estimate (the billing anchor; see db.quickDispatch) AND drops the job
// straight into the dispatch unassigned pool. Opened from:
//   • ClientIntakeModal — auto-offered right after saving a new client
//     with a quick job type + move date, and via the header button on
//     existing quick-type client files.
//   • DispatchBoard — clicking a NEW (no-estimate) card for a quick-type
//     client opens this instead of the bare New Job modal (which used to
//     create billing-orphan jobs with no rate anywhere).
// Rate prefills smart (DELIVERY = $125 FYN rate; others = config default),
// everything editable. Real moves keep the full estimate path.
//
// Moves (2026-09-18): the date row is the TripStrip (rule 1 — one date
// control everywhere; small job types show the single "Job day" tile).
// The date is NEVER seeded from client.move_date: on a client with history
// that column mirrors some older move and would bleed into this job. The
// only seed is `initialDate` (board click) or the client's current move when
// it is still a bare lead (fresh intake, no options yet, date not in the
// past) — that same move is the one db.quickDispatch attaches to; otherwise
// the db makes a new move.

const TYPE_LABELS = { DELIVERY: 'Delivery', DUMP_RUN: 'Dump Run', LABOR_ONLY: 'Labor Only', PICKUP: 'Pickup' };

export default function QuickDispatchModal({ client, initialDate, onClose, onDone, showToast }) {
    const { config, loadAll, moves, estimates, refreshMove } = useStore();
    const defaultRate = Number(config?.default_hourly_rate) || 225;

    // The client's current move, only when it is a bare lead (no options):
    // that is the move Quick Dispatch fills in. Anything else → new move.
    const bareLeadMove = (() => {
        const m = currentMoveFor(client, moves);
        if (!m || m.status !== 'lead') return null;
        return optionsForMove(estimates, m.id).length ? null : m;
    })();
    // Its date seeds the tile only while it is still ahead of us — a lead
    // move that sat for months with a past date is "an old move" for this
    // purpose (moves-architecture §4: Quick Dispatch never seeds from one).
    const seedDate = initialDate || (bareLeadMove?.load_date && bareLeadMove.load_date >= todayYmd() ? bareLeadMove.load_date : '');

    const startType = QUICK_DISPATCH_TYPES.includes(client?.job_type) ? client.job_type : 'DELIVERY';
    const seed = (t) => {
        const d = QUICK_DISPATCH_DEFAULTS[t] || {};
        return { rate: d.rate != null ? d.rate : defaultRate, hours: d.hours || 2, crew: d.crew || 2 };
    };

    const [form, setForm] = useState(() => {
        const s = seed(startType);
        return {
            job_type: startType,
            // TripStrip draft keys (load_* = the job day for small types).
            load_date: seedDate,
            load_time: initialDate || !seedDate ? '09:00' : (bareLeadMove?.load_time || '09:00'),
            load_tentative: initialDate || !seedDate ? false : !!bareLeadMove?.load_tentative,
            origin_address: client?.origin_address || '',
            destination_address: client?.destination_address || '',
            crew_size: s.crew,
            trucks_needed: client?.trucks_needed || '16ft',
            hourly_rate: s.rate,
            estimated_hours: s.hours,
            notes: '',
        };
    });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    // Switching type re-seeds rate/hours/crew ONLY if the user hasn't
    // customized them away from the previous type's seed values.
    const setType = (t) => {
        setForm(f => {
            const prev = seed(f.job_type);
            const next = seed(t);
            return {
                ...f,
                job_type: t,
                hourly_rate: Number(f.hourly_rate) === prev.rate ? next.rate : f.hourly_rate,
                estimated_hours: Number(f.estimated_hours) === prev.hours ? next.hours : f.estimated_hours,
                crew_size: Number(f.crew_size) === prev.crew ? next.crew : f.crew_size,
            };
        });
    };

    const total = (Number(form.hourly_rate) || 0) * (Number(form.estimated_hours) || 0);

    // TripStrip hands back only the changed keys — merge into the draft.
    const patchDraft = (patch) => setForm(f => ({ ...f, ...patch }));

    const handleSend = async () => {
        if (!form.load_date) { showToast?.('Pick a date for the job', 'error'); return; }
        if (!(Number(form.hourly_rate) > 0)) { showToast?.('Enter an hourly rate', 'error'); return; }
        setSaving(true);
        try {
            const res = await db.quickDispatch({
                client_id: client.id,
                // Only a bare lead move is reused; otherwise the db makes a new move.
                ...(bareLeadMove ? { move_id: bareLeadMove.id } : {}),
                job_type: form.job_type,
                move_date: form.load_date,
                time: form.load_time,
                origin_address: form.origin_address,
                destination_address: form.destination_address,
                crew_size: form.crew_size,
                trucks_needed: form.trucks_needed === 'none' ? '' : form.trucks_needed,
                hourly_rate: form.hourly_rate,
                estimated_hours: form.estimated_hours,
                notes: form.notes,
            });
            await loadAll?.();
            // The DB booked an option + flipped the client's current move; pull that move fresh.
            if (res.estimate?.move_id) await refreshMove?.(res.estimate.move_id);
            showToast?.(`${res.estimate?.job_number || 'Job'} is on the dispatch board — unassigned on ${new Date(form.load_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`);
            onDone?.(res.estimate);
            onClose();
        } catch (e) {
            showToast?.('Error: ' + e.message, 'error');
        } finally { setSaving(false); }
    };

    return (
        <Modal title={`Quick Dispatch — ${client?.name || 'Client'}`} onClose={onClose} width="max-w-2xl">
            <div className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs text-emerald-800">
                    <span className="font-bold">Small-job fast path.</span> This books the job and puts it straight in the dispatch unassigned pool — no estimate to build. Billing still works normally (rate × hours below is what Records and the invoice will use).
                </div>

                <FormField label="Job Type">
                    <ChoiceKeys items={QUICK_DISPATCH_TYPES.map(t => ({ value: t, label: TYPE_LABELS[t] }))} value={form.job_type} onChange={v => setType(v)} />
                </FormField>

                {/* One date control everywhere (moves rule 1). job_type on the draft → single "Job day" tile. */}
                <TripStrip value={form} onChange={patchDraft} label="Job date" forceUnload={false} forcePack={false} />

                <FormField label={form.job_type === 'DUMP_RUN' ? 'Pickup Address' : 'From'}>
                    <AddressInput value={form.origin_address} onChange={v => set('origin_address', v)} />
                </FormField>
                <FormField label={form.job_type === 'DUMP_RUN' ? 'Dump / Drop-off (optional)' : 'To'}>
                    <AddressInput value={form.destination_address} onChange={v => set('destination_address', v)} />
                </FormField>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <FormField label="Crew Size">
                        <NumberField value={form.crew_size} onChange={v => set('crew_size', v)} step={1} min={1} />
                    </FormField>
                    <FormField label="Est. Hours">
                        <NumberField value={form.estimated_hours} onChange={v => set('estimated_hours', v)} step={0.5} min={0.5} suffix="hrs" />
                    </FormField>
                    <FormField label="Rate ($/hr)">
                        <NumberField value={form.hourly_rate} onChange={v => set('hourly_rate', v)} step={5} min={0} prefix="$" />
                    </FormField>
                    <FormField label="Quoted">
                        <div className="px-3 py-2.5 text-sm font-bold text-green-700 bg-green-50 border border-green-200 rounded-lg">${total.toLocaleString()}</div>
                    </FormField>
                </div>

                <FormField label="Truck">
                    <ChoiceKeys items={TRUCK_TYPE_OPTIONS_COMPACT} value={form.trucks_needed} onChange={v => set('trucks_needed', v)} />
                </FormField>

                <FormField label="Notes for the Crew (optional)">
                    <input className={inputCls} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Gate code, what's being hauled, contact on site…" />
                </FormField>

                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <Button variant="tertiary" onClick={onClose}>Not now</Button>
                    <Button loading={saving} onClick={handleSend}>⚡ Send to Dispatch</Button>
                </div>
            </div>
        </Modal>
    );
}
